import {
    AfterViewChecked,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { Scene } from '../../models/scene';
import { SceneVisualService } from '../../services/scene-visual.service';
import { SceneStateService } from '../../services/scene-state.service';
import { filter, Observable, Subscription, take, tap } from 'rxjs';
import { nonNil } from 'src/app/shared/utility';

@Component({
    selector: 'app-edit-scene',
    templateUrl: './edit-scene.component.html',
    styleUrls: ['./edit-scene.component.scss'],
    providers: [SceneVisualService, SceneStateService],
    standalone: false,
})
export class EditSceneComponent
    implements OnInit, OnDestroy, AfterViewChecked, OnChanges
{
    @Input() trainingId: string;
    @Input() editingIsDisabled: boolean = false;

    @ViewChildren('dropTarget') dropTargetElements: QueryList<ElementRef>;
    @ViewChildren('dragSource') dragSourceElements: QueryList<ElementRef>;
    @ViewChild('container') containerElement: ElementRef;

    waitFor: Observable<Scene>;
    subscriptions: Subscription[] = [];
    robotId: string;
    initialized: boolean = false;
    isLoading: boolean = true;

    private resizeObserver: ResizeObserver;

    constructor(
        private sceneVisualService: SceneVisualService,
        private sceneStateService: SceneStateService
    ) {}

    ngOnInit(): void {
        this.sceneVisualService.trainingId = this.trainingId;

        this.waitFor = this.sceneStateService.observeScene$.pipe(
            filter(nonNil),
            take(1),
            tap((scene: Scene) => {
                if (!this.initialized) {
                    this.initialized = true;
                    this.robotId = scene.sceneElements.find(
                        (element) => element.browserAndPhysicsLoadable.isRobot
                    )?.browserAndPhysicsLoadable.uuid;
                    this.sceneStateService.isLoading$.next(false);
                    this.sceneStateService.changeDiversitySelection({
                        base: this.sceneStateService.scene$.value.base,
                        exampleMotion: undefined,
                    });
                    this.sceneStateService.scene$.next(
                        this.sceneStateService.scene$.value
                    );
                }
            })
        );
        this.subscriptions.push(this.waitFor.subscribe());
        this.subscriptions.push(
            this.sceneStateService.isLoading$.subscribe(
                (isLoading) => (this.isLoading = isLoading)
            )
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.editingIsDisabled &&
            !changes.editingIsDisabled.isFirstChange()
        ) {
            if (this.editingIsDisabled) {
                this.sceneVisualService.disableEditing();
            } else {
                this.sceneVisualService.enableEditing();
            }
        }
    }

    ngOnDestroy(): void {
        this.sceneVisualService.destroyScene();
        this.resizeObserver.disconnect();
        this.subscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
    }

    @ViewChild('container')
    set container(container: ElementRef) {
        if (container !== undefined) {
            this.sceneVisualService.initialize(container.nativeElement);

            if (this.editingIsDisabled)
                this.sceneVisualService.disableEditing();
        }
    }

    ngAfterViewChecked(): void {
        if (!this.resizeObserver && this.containerElement) {
            this.resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const contentRect = entry.contentRect;
                    this.sceneVisualService.onWindowResize(
                        contentRect.width,
                        contentRect.height
                    );
                }
            });
            this.resizeObserver.observe(this.containerElement.nativeElement);
        }
    }
}
